"use client"

import { addVoucher } from "@/actions/selection/client/add-voucher"
import { removeVoucher } from "@/actions/selection/client/remove-voucher"
import { type ProductNoticesCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/productNoticesCopy"
import OrderSummaryProductDetails from "@/app/(site)/[locale]/checkout/CheckoutContent/components/OrderSummery/OrderSummaryProductDetails"
import OrderSummaryTitle from "@/app/(site)/[locale]/checkout/CheckoutContent/components/OrderSummery/OrderSummaryTitle"
import { PriceTotal } from "@/app/(site)/[locale]/checkout/CheckoutContent/components/PriceTotals"
import CustomerService from "@/app/(site)/[locale]/checkout/CheckoutContent/components/Sanity/CustomerService"
import {
    AccordionContent,
    AccordionContext,
    AccordionIcon,
    AccordionRoot,
    AccordionTrigger
} from "@/app/(site)/_components/Atoms/Accordion"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import { IconPlus } from "@/app/(site)/_components/Atoms/Icons"
import InputVoucher from "@/app/(site)/_components/Atoms/Inputs/InputVoucher"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { usePaymentResultEvent } from "@/app/(site)/_utils/_hooks/usePaymentResultEvent"
import { bunnyLoader } from "@/lib/sanityLoader"
import { FREE_SHIPPING_VOUCHER_CODE, MAX_VOUCHERS } from "@/lib/siteconfig/constants"
import { hasFreeShippingVoucher } from "@/lib/utils/checkout/shippingUtils"
import {
    calculateTotalVoucherDiscount,
    extractVouchersFromSelection,
    isVoucherCodeAppliedInSession
} from "@/lib/utils/checkout/voucherUtils"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { type CheckoutPageSettingsContent } from "@/types"
import { type BasketSessionState } from "@/types/centra/basket-session-state"
import placeholder from "@assets/images/placeholder.png"
import { useContext, useState } from "react"

function CartImagePreview({ products }: { products: any[] }) {
    const context = useContext(AccordionContext)

    if (!context) {
        return null
    }

    const { isOpen } = context

    return (
        <div
            className={clsx(
                "grid",
                isOpen
                    ? "grid-rows-[0fr] opacity-0 transition-all duration-300 ease-in-out"
                    : "grid-rows-[1fr] opacity-100 transition-all duration-300 ease-in-out"
            )}
        >
            <div className="overflow-hidden">
                <div className="flex gap-x-4 overflow-x-auto px-12 pb-12 scrollbar-hide sm:px-16">
                    {products.length > 0 &&
                        products.map(({ product }: any, index: number) => {
                            const productImage =
                                (product.media as Record<string, string[]>)?.[
                                    "standard"
                                ]?.[0] ?? placeholder.src
                            return (
                                <DynamicLink
                                    key={`${product?.uri}-${index}`}
                                    href={`/product/${product?.uri || ""}`}
                                    prefetch={false}
                                    className="relative flex aspect-[0.7] h-[64px] w-[45px] items-center justify-center bg-col-bg-calm"
                                >
                                    <BaseImage
                                        src={bunnyLoader({
                                            src: productImage,
                                            width: 120,
                                            quality: 80
                                        })}
                                        alt={product?.name || "Product"}
                                        fill
                                        className="h-full w-full object-contain"
                                    />
                                </DynamicLink>
                            )
                        })}
                </div>
            </div>
        </div>
    )
}

interface OrderSummaryProps {
    showVoucherInput?: boolean
    showShipping?: boolean
    className?: string
    langCode: string
    customerService: CheckoutPageSettingsContent["customerService"] | null
    marketLayout: string
    productNoticesCopy: ProductNoticesCopy
}

export default function OrderSummary({
    showVoucherInput = true,
    showShipping = true,
    className = "",
    langCode,
    customerService,
    marketLayout,
    productNoticesCopy
}: OrderSummaryProps) {
    const [voucherCode, setVoucherCode] = useState("")
    const [voucherLoading, setVoucherLoading] = useState(false)
    const [voucherErrors, setVoucherErrors] = useState<string[]>([])
    const isRoutingToPaymentResult = usePaymentResultEvent()
    const { selection, isLoadingSelection, mutate } = useSWRBasketSessionState()
    const items = selection?.items
    const products = Array.isArray(items) ? items : []
    const itemCount = products.reduce(
        (acc: number, product: any) => acc + product.quantity,
        0
    )
    const totals = selection?.totals
    const rawVouchers = selection?.discounts?.vouchers
    const vouchers = extractVouchersFromSelection(
        Array.isArray(rawVouchers) ? rawVouchers : []
    )
    const rawAutomaticDiscounts = selection?.discounts?.automaticDiscounts
    const automaticDiscounts = Array.isArray(rawAutomaticDiscounts)
        ? rawAutomaticDiscounts.filter(
              discount => (discount.originalPriceOffAsNumber ?? 0) !== 0
          )
        : []
    const hasMaxVouchers = vouchers.length >= MAX_VOUCHERS

    const totalDiscountVoucherDisplay = calculateTotalVoucherDiscount([
        ...vouchers,
        ...automaticDiscounts
    ])

    const handleAddVoucher = async () => {
        if (!voucherCode.trim()) return
        setVoucherLoading(true)
        setVoucherErrors([])

        const voucher = voucherCode.trim()

        try {
            const result = await addVoucher({ voucher })

            if (result.success) {
                await mutate()
                await import("@/lib/utils/tracking/gtmEvents").then(
                    ({ addVoucherEvent }) => addVoucherEvent(voucher)
                )
                setVoucherCode("")
                setVoucherErrors([])
            } else {
                setVoucherErrors([
                    t(langCode, "checkout.order_summary.invalid_voucher_code")
                ])
            }
        } catch (error) {
            setVoucherErrors([t(langCode, "checkout.order_summary.invalid_voucher_code")])
        } finally {
            setVoucherLoading(false)
        }
    }

    const handleRemoveVoucher = async (voucherToRemove: string) => {
        setVoucherLoading(true)
        setVoucherErrors([])

        const showRemoveFailedError = (session: BasketSessionState | undefined) => {
            if (!isVoucherCodeAppliedInSession(session, voucherToRemove)) {
                return
            }

            setVoucherErrors([
                t(langCode, "checkout.order_summary.voucher_remove_failed")
            ])
        }

        try {
            await mutate()
            const result = await removeVoucher({ voucher: voucherToRemove })

            if (result.success) {
                await mutate()
                return
            }

            showRemoveFailedError((await mutate()) as BasketSessionState | undefined)
        } catch (error) {
            showRemoveFailedError((await mutate()) as BasketSessionState | undefined)
        } finally {
            setVoucherLoading(false)
        }
    }

    const filteredVouchers = Array.isArray(vouchers)
        ? vouchers.filter(
              voucher =>
                  voucher.voucher.toLowerCase() !==
                  FREE_SHIPPING_VOUCHER_CODE.toLowerCase()
          )
        : []

    const filteredDiscountsForTotal = [...filteredVouchers, ...automaticDiscounts]

    const hasFreeShipping = hasFreeShippingVoucher(vouchers)

    return (
        <div
            className={clsx(
                `flex flex-col justify-between border-b border-col-border bg-col-bg-soft sm:border-l sm:border-r md:border-b-0 md:border-l-0`,
                className
            )}
        >
            <div className="border-b border-col-border">
                <div className="hidden md:block">
                    <OrderSummaryTitle langCode={langCode} itemCount={itemCount} />
                    <div
                        className={clsx(
                            `relative flex max-h-[400px] flex-col gap-y-24 overflow-y-auto border-b border-col-border py-12 scrollbar sm:py-16 md:py-24 ${products.length > 1 ? "pb-0" : ""}`,
                            products.length === 1 && "!pb-0"
                        )}
                    >
                        <OrderSummaryProductDetails
                            products={products}
                            langCode={langCode}
                            voucherLoading={voucherLoading}
                            isLoadingSelection={isLoadingSelection}
                            oneProduct={products.length === 1}
                            productNoticesCopy={productNoticesCopy}
                        />
                    </div>
                </div>

                <AccordionRoot
                    initalState={false}
                    accordionId="order-summary-cart"
                    accordionName="Order Summary Cart"
                    className="md:hidden"
                >
                    <div className="flex items-center justify-between border-b border-col-border">
                        <OrderSummaryTitle langCode={langCode} itemCount={itemCount} />
                    </div>
                    <AccordionTrigger className="flex w-full items-center gap-8 Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                        <AccordionIcon
                            openIcon={
                                <>
                                    {t(langCode, "checkout.order_summary.hide_cart")}
                                    <div className="h-20 w-20 pr-2">
                                        <IconPlus className="ml-auto h-full w-full rotate-45 transition-transform duration-300" />
                                    </div>
                                </>
                            }
                            closeIcon={
                                <>
                                    {t(langCode, "checkout.order_summary.show_cart")}
                                    <div className="h-20 w-20 pr-2">
                                        <IconPlus className="ml-auto h-full w-full transition-transform duration-300" />
                                    </div>
                                </>
                            }
                            className="flex w-full items-center justify-between px-12 py-12 sm:px-16"
                        />
                    </AccordionTrigger>

                    <CartImagePreview products={products} />

                    <AccordionContent
                        className={clsx(
                            "border-b border-col-border",
                            products.length === 1 && "!pb-0"
                        )}
                    >
                        <div
                            className={clsx(
                                "flex max-h-[400px] flex-col gap-y-24 overflow-y-auto pt-12 scrollbar sm:pt-16",
                                products.length === 1 && "!pb-0"
                            )}
                        >
                            <OrderSummaryProductDetails
                                products={products}
                                langCode={langCode}
                                voucherLoading={voucherLoading}
                                isLoadingSelection={isLoadingSelection}
                                oneProduct={products.length === 1}
                                productNoticesCopy={productNoticesCopy}
                            />
                        </div>
                    </AccordionContent>
                </AccordionRoot>

                {showVoucherInput && (
                    <div className="px-12 pt-24 sm:px-16 md:px-24 md:pt-40">
                        <InputVoucher
                            langCode={langCode}
                            voucherCode={voucherCode}
                            setVoucherCode={setVoucherCode}
                            voucherLoading={voucherLoading}
                            handleAddVoucher={() => !hasMaxVouchers && handleAddVoucher()}
                            handleRemoveVoucher={handleRemoveVoucher}
                            filteredVouchers={filteredVouchers}
                            automaticVouchers={automaticDiscounts}
                            errors={voucherErrors}
                            setErrors={setVoucherErrors}
                            disabled={
                                hasMaxVouchers ||
                                isLoadingSelection ||
                                isRoutingToPaymentResult
                            }
                            hasMaxVouchersMessage={t(
                                langCode,
                                "checkout.order_summary.max_vouchers_message"
                            )}
                            hasMaxVouchers={hasMaxVouchers}
                            isLoadingSelection={isLoadingSelection}
                        />
                    </div>
                )}
                <PriceTotal
                    langCode={langCode}
                    totals={totals}
                    showShipping={showShipping}
                    filteredVouchers={filteredDiscountsForTotal}
                    totalDiscountVoucherDisplay={totalDiscountVoucherDisplay}
                    marketLayout={marketLayout}
                    hasFreeShipping={hasFreeShipping}
                    isLoadingSelection={isLoadingSelection}
                />
            </div>
            {customerService && <CustomerService customerService={customerService} />}
        </div>
    )
}
